import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import "@styles/pages/Redeem/step2/RedeemPage.scss";
import { getOfferDetail, submitRedeemOffer } from "@api/redeemApi";
import { getMemberProfile } from "@api/memberApi";

import { Trans, useTranslation } from "react-i18next";
import { Box, MenuItem, Select, Typography, TextField } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { setFormStep } from "@store/ui/formStepSlice";
import { setRedeemGiftDetailData } from "@store/redeem/redeemSlice";
import { setMemberInfoData } from "@store/member/memberInfoSlice";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { toast } from "react-toastify";
import useScrollToTop from "@hooks/useScrollToTop";
import AccordionRow from "@components/ui/AccordionRow";
import Popup from "reactjs-popup";

const RedeemPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  useScrollToTop();
  const isChinese = i18n.language === "hk";
  // const memberInfoData = useSelector(
  //   (state) => state.memberInfo.memberInfoData
  // );
  const redeemDetail = useSelector(
    (state) => state.redeemGiftDetail.redeemGiftDetailData
  );
  const tncList = t("redeem:tnc_list", { returnObjects: true });

  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [tncText, setTncText] = useState({ hk: "", hk_en: "" });
  const [openPopup, setOpenPopup] = useState(false);

  const decodeHTMLWithLineBreak = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html; // Decode &lt; &gt; etc. into real HTML tags
    return div.innerText.trim(); // innerText 會保留 <br> 為換行符號 (\n)
  };

  useEffect(() => {
    const fetchOfferDetail = async () => {
      try {
        const res = await getOfferDetail({ redeem_id: redeemDetail.id });
        const data = res.data.Data;

        // 合併原本的 + 新的 data
        const mergedData = {
          ...redeemDetail,
          ...data.inhand_qty,
        };

        dispatch(setRedeemGiftDetailData(mergedData));
        setTncText({
          hk: decodeHTMLWithLineBreak(redeemDetail.desc_hk),
          hk_en: decodeHTMLWithLineBreak(redeemDetail.desc_hk_en),
        });
      } catch (err) {
        console.error(err);
      }
    };
    fetchOfferDetail();
  }, []);

  const calculateTotalPoint = () => {
    const point = Number(redeemDetail?.point_required) || 0;
    const qty = Number(quantity) || 0;
    return point * qty;
  };

  const totalPoint = calculateTotalPoint();

  const onSubmitData = () => {
    const finalData = {
      redeem_item_id: redeemDetail.id,
      quantity: quantity,
    };
    const postRedeemOffer = async () => {
      setIsLoading(true);
      try {
        const res = await submitRedeemOffer(finalData);
        const statusCode = res.data.StatusCode;
        switch (statusCode) {
          case 200:
            const profileRes = await getMemberProfile();
            dispatch(setMemberInfoData(profileRes.data.Data));
            toast.success(t("status_message:status_code_200_redeem_offer"));
            dispatch(setFormStep({ formKey: "redeem", step: 2 }));
            break;
          case 301:
            toast.error(t("status_message:status_code_301"));
            dispatch(setFormStep({ formKey: "redeem", step: 0 }));
            break;
          case 302:
            toast.error(t("status_message:status_code_302"));
            dispatch(setFormStep({ formKey: "redeem", step: 0 }));
            break;
          case 500:
            toast.error(t("status_message:status_code_500"));
            dispatch(setFormStep({ formKey: "redeem", step: 0 }));
            break;
          case 601:
            toast.error(t("status_message:status_code_601"));
            dispatch(setFormStep({ formKey: "redeem", step: 0 }));
            break;
          case 602:
            toast.error(t("status_message:status_code_602"));
            dispatch(setFormStep({ formKey: "redeem", step: 0 }));
            break;
          case 603:
            toast.error(t("status_message:status_code_603"));
            dispatch(setFormStep({ formKey: "redeem", step: 0 }));
            break;
          case 604:
            toast.error(t("status_message:status_code_604"));
            dispatch(setFormStep({ formKey: "redeem", step: 0 }));
            break;
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    postRedeemOffer();
  };
  return (
    <Box className="redeem-page-container">
      {/* <Typography className="title">
        <Trans i18nKey="redeem:title" />
      </Typography> */}
      <Box className="detail-container">
        <Box className="image-container">
          <img className="redeem-image" src={redeemDetail?.image} />
        </Box>
        <Box className="gift-container">
          <Box className="gift-detail-container">
            <Typography className="gift-name">
              {isChinese ? redeemDetail?.title_hk : redeemDetail?.title_hk_en}
            </Typography>
            <Typography className="gift-date">
              <Trans i18nKey="redeem:redemption_period" />
              {redeemDetail?.start_date} - {redeemDetail?.end_date}
            </Typography>
          </Box>

          <Typography className="gift-detail">
            <Trans i18nKey="redeem:use_points" />
            {totalPoint.toLocaleString()} <Trans i18nKey="redeem:s_dollars" />
          </Typography>
          <Typography className="quantity-container">
            <Trans i18nKey="redeem:quantity" />
            <RemoveCircleIcon
              className="quantity-icon"
              onClick={() => {
                setQuantity((prev) => Math.max(prev - 1, 1));
              }}
            />
            <Typography className="quantity-text">{quantity}</Typography>
            <AddCircleIcon
              className="quantity-icon"
              onClick={() => {
                setQuantity((prev) => Math.min(prev + 1, 5));
              }}
            />
          </Typography>

          <Box className="button-container">
            <LoadingButton
              disableElevation
              disableRipple
              className="button"
              onClick={() =>
                dispatch(setFormStep({ formKey: "redeem", step: 0 }))
              }
              disabled={isLoading}
              loading={isLoading}
            >
              <Trans i18nKey="redeem:back"></Trans>
            </LoadingButton>
            <LoadingButton
              onClick={() => setOpenPopup(true)}
              className="loading-button"
              disabled={isLoading}
              loading={isLoading}
            >
              <Trans i18nKey="redeem:redeem" />
            </LoadingButton>
          </Box>
        </Box>
      </Box>
      <Box className="redemption-policy-container">
        <AccordionRow
          summaryTitle={<Trans i18nKey="redeem:redemption_policy" />}
          details={[
            {
              value: (
                <Box className="tnc">
                  {/* {tncList.map((text, idx) => (
                    <Box key={idx}>{text}</Box>
                  ))}
                 */}
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html:isChinese? tncText.hk: tncText.hk_en,
                    }}
                  />
                </Box>
              ),
            },
          ]}
        />
      </Box>{" "}
      {/* Popup 視窗 */}
      <Popup open={openPopup} modal onClose={() => setOpenPopup(false)}>
        {(close) => (
          <Box className="popup-container">
            <Typography className="popup-title">
              <Trans i18nKey="redeem:confirm_to_redeem" />
            </Typography>
            <Box className="popup-button-container">
              <LoadingButton
                className="button"
                onClick={() => {
                  setOpenPopup(false);
                  close();
                }}
                loading={isLoading}
                disabled={isLoading}
              >
                <Trans i18nKey="form:no" />
              </LoadingButton>
              <LoadingButton
                className="loading-button"
                onClick={() => onSubmitData()}
                loading={isLoading}
                disabled={isLoading}
              >
                <Trans i18nKey="inquiry:yes" />
              </LoadingButton>
            </Box>
          </Box>
        )}
      </Popup>
    </Box>
  );
};

export default RedeemPage;
